import React, { useState, useEffect } from 'react';
import DeckBuilder from "../components/DeckBuilder";
import { useAuth } from "../contexts/AuthContext";
import { getLocalDecks, saveLocalDeck, deleteLocalDeck, syncLocalDecksToServer } from "../utils/localDeckStorage";

export default function Decks({ cards }) {
  const { user, loading: authLoading } = useAuth();
  const [serverDecks, setServerDecks] = useState([]);
  const [localDecks, setLocalDecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [syncStatus, setSyncStatus] = useState({ syncing: false, message: '' });

  // Fetch user's saved decks from server and local storage
  useEffect(() => {
    const fetchDecks = async () => {
      setLoading(true);

      // Get local decks
      const localDecks = getLocalDecks();
      setLocalDecks(localDecks);

      if (user) {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const responseJson = await response.json();
            setServerDecks(responseJson.decks || []);
          } else {
            console.error('Failed to fetch server decks');
            // Still show local decks if server fetch fails
          }
        } catch (error) {
          console.error('Error fetching server decks:', error);
        }
      } else {
        // If not logged in, just show local decks
        setServerDecks([]);
      }

      setLoading(false);
    };

    fetchDecks();

    // Listen for online/offline events to handle sync
    const handleOnline = () => {
      if (user && localDecks.length > 0) {
        syncLocalDecks();
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [user]);

  const syncLocalDecks = async () => {
    if (!user) return;

    setSyncStatus({ syncing: true, message: 'Syncing decks...' });

    try {
      const results = await syncLocalDecksToServer(serverDecks);
      const successfulSyncs = results.filter(r => r.success).length;

      if (successfulSyncs > 0) {
        setSyncStatus({
          syncing: false,
          message: `Synced ${successfulSyncs} deck(s) to server`
        });

        // Refresh server decks after sync
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const responseJson = await response.json();
          setServerDecks(responseJson.decks || []);
        }
      } else {
        setSyncStatus({ syncing: false, message: 'No decks to sync' });
      }
    } catch (error) {
      setSyncStatus({
        syncing: false,
        message: 'Sync failed: ' + error.message
      });
    }
  };

  const loadDeck = (deck) => {
    // Switch to the builder tab and load the deck
    setActiveTab('builder');
    // The DeckBuilder component will handle loading the deck via its loadDeckForEditing function
    // This will be passed down through props
  };

  const loadDeckForEditingFromPage = (deck) => {
    setActiveTab('builder');
    // The DeckBuilder component will handle loading the deck via its loadDeckForEditing function
    // This will be passed down through props
  };

  // Combine server and local decks
  const allDecks = [...serverDecks, ...localDecks.map(d => ({ ...d, isLocal: true }))];

  return (
    <div className={activeTab === 'builder' ? 'pt-2 h-[100vh] overflow-hidden flex flex-col' : 'py-8'}>
      <div className="px-4 sm:px-6 lg:px-8 flex flex-col flex-1 min-h-0">

        {user && (
          <div className="mb-6 border-b border-border">
            <nav className="flex space-x-6">
              <button
                className={`pb-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'builder'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('builder')}
              >
                Deck Builder
              </button>
              <button
                className={`pb-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'saved'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('saved')}
              >
                Saved Decks ({allDecks.length})
              </button>
            </nav>
          </div>
        )}

        {!user ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <h2 className="text-2xl font-bold mb-4">Sign In to Access Your Decks</h2>
            <p className="text-muted-foreground mb-8 max-w-md">
              Create and save your custom card decks. Sign in to access your collection and start building your perfect deck.
            </p>
            <button
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg text-lg font-medium hover:bg-primary/90"
              onClick={() => window.dispatchEvent(new CustomEvent('openLoginModal'))}
            >
              Sign In to Continue
            </button>
          </div>
        ) : (
          <>
            {activeTab === 'builder' ? (
              <DeckBuilder cards={cards} />
            ) : (
              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Loading your decks...</p>
                  </div>
                ) : allDecks.length > 0 ? (
                  <>
                    {syncStatus.message && (
                      <div className={`p-3 rounded-lg text-center ${
                        syncStatus.syncing ? 'bg-blue-500/20 border border-blue-500/50' :
                        syncStatus.message.includes('failed') ? 'bg-red-500/20 border border-red-500/50' :
                        'bg-green-500/20 border border-green-500/50'
                      }`}>
                        {syncStatus.message}
                        {syncStatus.syncing && <span className="ml-2">Syncing...</span>}
                      </div>
                    )}

                    <div className="flex justify-between">
                      <button
                        className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm hover:bg-primary/90"
                        onClick={async () => {
                          const deckName = prompt('Enter deck name:');
                          if (deckName) {
                            try {
                              // Create a new deck with just a name (draft deck)
                              const newDeck = {
                                name: deckName,
                                description: `New deck: ${deckName}`,
                                cards: {
                                  legend: null,
                                  champions: [],
                                  mainCards: [],
                                  battlefields: [],
                                  runes: []
                                }
                              };

                              const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                                },
                                body: JSON.stringify(newDeck)
                              });

                              const result = await response.json();

                              if (response.ok) {
                                // Add the new deck to the list
                                setServerDecks(prev => [...prev, result.deck]);

                                // Load the new deck in the builder
                                localStorage.setItem('deckToLoad', JSON.stringify(result.deck));
                                setActiveTab('builder');
                                window.dispatchEvent(new CustomEvent('loadDeckForEditing', { detail: result.deck }));
                              } else {
                                alert(result.message || 'Failed to create deck');
                              }
                            } catch (error) {
                              console.error('Error creating deck:', error);
                              alert('Network error occurred while creating deck');
                            }
                          }
                        }}
                      >
                        Create Deck
                      </button>
                      <button
                        className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm hover:bg-primary/90"
                        onClick={syncLocalDecks}
                        disabled={syncStatus.syncing || localDecks.length === 0}
                      >
                        {syncStatus.syncing ? 'Syncing...' : 'Sync Local Decks'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {allDecks.map((deck) => (
                        <div
                          key={deck.id || deck.localId}
                          className={`bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 ${
                            deck.isLocal ? 'ring-1 ring-yellow-500/50' : ''
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <h3 className="font-bold text-lg mb-2">{deck.name}</h3>
                            {deck.isLocal && (
                              <span className="text-xs bg-yellow-500/20 text-yellow-600 px-2 py-1 rounded">
                                Local
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-muted-foreground mb-4">{deck.description}</p>

                          <div className="text-xs text-muted-foreground mb-4">
                            {deck.isLocal
                              ? `Created: ${new Date(deck.createdAt).toLocaleDateString()}`
                              : `Created: ${new Date(deck.created_at).toLocaleDateString()}`}
                          </div>

                          <div className="flex gap-2">
                            <button
                              className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm hover:bg-primary/90"
                              onClick={() => {
                                // Update state to indicate we want to load this deck
                                localStorage.setItem('deckToLoad', JSON.stringify(deck));
                                setActiveTab('builder');
                                // Trigger a window event to notify the DeckBuilder
                                window.dispatchEvent(new CustomEvent('loadDeckForEditing', { detail: deck }));
                              }}
                            >
                              Edit
                            </button>

                            <button
                              className={`px-3 py-1 rounded text-sm ${
                                deck.isLocal
                                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                                  : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                              }`}
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete deck "${deck.name}"?`)) {
                                  if (deck.isLocal) {
                                    // Delete local deck
                                    const success = deleteLocalDeck(deck.localId || deck.id);
                                    if (success) {
                                      alert('Local deck deleted successfully!');
                                      setLocalDecks(prev => prev.filter(d => d.localId !== deck.localId && d.id !== deck.id));
                                    } else {
                                      alert('Failed to delete local deck');
                                    }
                                  } else {
                                    // Delete server deck
                                    try {
                                      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks/${deck.id}`, {
                                        method: 'DELETE',
                                        headers: {
                                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                                        }
                                      });

                                      if (response.ok) {
                                        alert('Deck deleted successfully!');
                                        // Refresh the saved decks list
                                        const refreshResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
                                          headers: {
                                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                                          }
                                        });

                                        if (refreshResponse.ok) {
                                          const responseJson = await refreshResponse.json();
                                          setServerDecks(responseJson.decks || []);
                                        }
                                      } else {
                                        alert('Failed to delete deck');
                                      }
                                    } catch (error) {
                                      console.error('Error deleting deck:', error);
                                      alert('Network error occurred while deleting deck');
                                    }
                                  }
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">You haven't saved any decks yet.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}